import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  MenuItem,
} from '@mui/material';
import { Sms as SmsIcon } from '@mui/icons-material';
import { useDueLoans, useSendReminder } from '../../hooks/collections/useCollections';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

const Collections: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [daysOverdueFilter, setDaysOverdueFilter] = useState<number | undefined>(undefined);
  const { data: dueLoans, isLoading, error } = useDueLoans(daysOverdueFilter);
  const sendReminderMutation = useSendReminder();

  const handleSendReminder = async (loan: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    try {
      await sendReminderMutation.mutateAsync({
        customerId: loan.customerId,
        loanId: loan.loanId,
        reminderType: 'SMS',
      });
      enqueueSnackbar('Reminder sent successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to send reminder',
        { variant: 'error' }
      );
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Collections
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load due loans. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Collections
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage overdue loans and payment reminders
          </Typography>
        </Box>

        <TextField
          select
          label="Filter by Days Overdue"
          value={daysOverdueFilter || ''}
          onChange={(e) => setDaysOverdueFilter(e.target.value ? Number(e.target.value) : undefined)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="7">7+ days</MenuItem>
          <MenuItem value="14">14+ days</MenuItem>
          <MenuItem value="30">30+ days</MenuItem>
        </TextField>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Loan Number</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Outstanding</strong></TableCell>
              <TableCell><strong>Days Overdue</strong></TableCell>
              <TableCell><strong>Due Date</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : dueLoans?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No overdue loans</Typography>
                </TableCell>
              </TableRow>
            ) : (
              dueLoans?.map((loan) => (
                <TableRow key={loan.loanId} hover>
                  <TableCell>{loan.loanNumber}</TableCell>
                  <TableCell>
                    {loan.customerName}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {loan.customerPhone}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatCurrency(loan.amountOutstanding)}</TableCell>
                  <TableCell>
                    <Chip
                      label={`${loan.daysOverdue} days`}
                      size="small"
                      color={loan.daysOverdue > 30 ? 'error' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    {loan.dueDate ? format(new Date(loan.dueDate), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SmsIcon />}
                      onClick={() => handleSendReminder(loan)}
                      disabled={sendReminderMutation.isPending}
                    >
                      Send Reminder
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Collections;
